import React, { useState, useEffect } from "react";
import { FaUser, FaTrashAlt, FaEdit, FaEye, FaSearch } from "react-icons/fa";

import { useAllUsers, useDeleteUser } from "../../hooks/useAdmin.ts";
import Pagination from "../../components/Pagination.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

const initialUsers: User[] = [
  { id: 1, name: "loading...", email: "loading...", role: "loading..." },
];

const UserList: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);

  const { data: usersData, isLoading: isLoadingUsers, error: usersError } = useAllUsers();
  const deleteMutation = useDeleteUser();

  const [users, setUsers] = useState<User[]>(initialUsers);

  useEffect(() => {
    if (usersData) {
      setUsers(usersData);
    }
  }, [usersData]);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [sorting, setSorting] = useState<SortingState>([]);

  const filteredUsers = users.filter(
    (user) =>
      (user.name?.toLowerCase?.() ?? "").includes(searchQuery.toLowerCase()) ||
      (user.email?.toLowerCase?.() ?? "").includes(searchQuery.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const rawData = localStorage.getItem("user");
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;

  /* User fetching handled by useAllUsers hook */

  const handleProfile = (user: User) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setUpdateModalOpen(true);
  };

  const handleDelete = (userId: number) => {
    setUserToDelete(userId);
    setDeletePopupOpen(true);
  };

  const confirmDelete = async () => {
    if (userToDelete === null) return;

    setIsDeleting(true);
    setError("");

    try {
      await deleteMutation.mutateAsync(userToDelete, {
        onSuccess: () => {
          setUsers(users.filter((user) => user.id !== userToDelete));
          setDeletePopupOpen(false);
          setUserToDelete(null);
        },
        onError: (err: any) => {
          console.error("Delete failed:", err);
          setError(err?.response?.data?.message || "Failed to delete user. Please try again.");
        }
      });
    } catch (err) {
      // handled in onError
    } finally {
      setIsDeleting(false);
    }
  };

  const updateUser = (updatedUser: { name: string; email: string; role: string }) => {
    if (selectedUser) {
      setUsers(
        users.map((user) =>
          user.id === selectedUser.id ? { ...user, ...updatedUser } : user
        )
      );
      setUpdateModalOpen(false);
    }
  };

  // TanStack Table column definitions
  const columnHelper = createColumnHelper<User>();

  const columns: ColumnDef<User, any>[] = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => (
        <div className="flex items-center">
          <FaUser className="mr-2" style={{ color: currentTheme.primary }} />
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: (props) => (
        <div className="text-right">
          <button
            className="mr-2"
            style={{ color: currentTheme.primary }}
            onClick={() => handleProfile(props.row.original)}
          >
            <FaEye />
          </button>
          <button
            className="mr-2"
            style={{ color: currentTheme.primary }}
            onClick={() => handleEdit(props.row.original)}
          >
            <FaEdit />
          </button>
          <button
            className={`${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ color: currentTheme.error }}
            onClick={() => handleDelete(props.row.original.id)}
            disabled={isDeleting}
          >
            <FaTrashAlt />
          </button>
        </div>
      ),
    }),
  ];

  // TanStack Table instance
  const table = useReactTable({
    data: filteredUsers,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // We handle pagination manually
    pageCount: totalPages,
  });

  // Get sorted rows from TanStack Table, then apply manual pagination
  const sortedRows = table.getRowModel().rows;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  return (
    <div className="pb-4">
      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 px-4 py-3 rounded" style={{ backgroundColor: `${currentTheme.error}20`, border: `1px solid ${currentTheme.error}`, color: currentTheme.error }}>
          {error}
        </div>
      )}
      <div className="flex items-center p-1 rounded-lg mb-2 w-1/2" style={{ borderColor: currentTheme.border }}>
        <input
          type="text"
          className="w-full px-2 py-1 focus:outline-none"
          style={{ backgroundColor: currentTheme.surface, color: currentTheme.text }}
          placeholder="Search by name or email"
          value={searchQuery}
          onChange={handleSearch}
        />
        <div className="px-3 h-full flex items-center" style={{ backgroundColor: currentTheme.card }}>
          <FaSearch style={{ color: currentTheme.textSecondary }} />
        </div>
      </div>
      <div className="shadow-lg rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
        {/* Table Container - No scrollbar, displays only current page */}

        <div>
          <table className="min-w-full">
            <thead className="sticky top-0 z-10" style={{ backgroundColor: currentTheme.primary }}>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      className={`p-3 text-left ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-opacity-10' : ''}`}
                      style={{
                        color: currentTheme.inverseText,
                        backgroundColor: header.column.getIsSorted() ? 'rgba(255,255,255,0.1)' : 'transparent'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="flex flex-col leading-none" style={{ color: currentTheme.inverseText, fontSize: '10px' }}>
                            <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.3 }}>▲</span>
                            <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.3 }}>▼</span>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paginatedRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b transition duration-200"
                  style={{
                    borderColor: currentTheme.border,
                    backgroundColor: hoveredRow === row.original.id ? (themeName === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)') : currentTheme.surface
                  }}
                  onMouseEnter={() => setHoveredRow(row.original.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="p-3" style={{ color: currentTheme.text }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
          {/* Rows per page selector */}
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: currentTheme.textSecondary }}>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
              className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: currentTheme.surface,
                color: currentTheme.text,
                borderColor: currentTheme.border
              }}
            >
              <option value={12}>12</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm" style={{ color: currentTheme.textMuted }}>
              Showing {filteredUsers.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </span>
          </div>

          {/* Pagination component */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </div>

      {/* User Profile Modal */}
      {showProfileModal && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="p-6 rounded-lg shadow-lg w-96" style={{ backgroundColor: currentTheme.surface }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: currentTheme.text }}>User Profile</h2>
            <p style={{ color: currentTheme.text }}><strong style={{ color: currentTheme.primary }}>Name:</strong> {selectedUser.name}</p>
            <p style={{ color: currentTheme.text }}><strong style={{ color: currentTheme.primary }}>Email:</strong> {selectedUser.email}</p>
            <p style={{ color: currentTheme.text }}><strong style={{ color: currentTheme.primary }}>Role:</strong> {selectedUser.role}</p>
            <button
              className="mt-4 px-4 py-2 rounded"
              style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
              onClick={() => setShowProfileModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {isUpdateModalOpen && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="p-6 rounded-lg shadow-lg w-96" style={{ backgroundColor: currentTheme.surface }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: currentTheme.text }}>Update User</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateUser({
                  name: (e.target as any).name.value,
                  email: (e.target as any).email.value,
                  role: (e.target as any).role.value,
                });
              }}
            >
              <div className="mb-4">
                <label style={{ color: currentTheme.text }}>Name:</label>
                <input name="name" defaultValue={selectedUser.name} className="w-full p-2 rounded" style={{ backgroundColor: currentTheme.surface, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }} required />
              </div>
              <div className="mb-4">
                <label style={{ color: currentTheme.text }}>Email:</label>
                <input name="email" defaultValue={selectedUser.email} className="w-full p-2 rounded" style={{ backgroundColor: currentTheme.surface, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }} required />
              </div>
              <div className="mb-4">
                <label style={{ color: currentTheme.text }}>Role:</label>
                <select name="role" defaultValue={selectedUser.role} className="w-full p-2 rounded" style={{ backgroundColor: currentTheme.surface, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}>
                  <option value="Admin">Admin</option>
                  <option value="User">User</option>
                </select>
              </div>
              <div className="flex justify-end">
                <button type="submit" className="px-4 py-2 rounded mr-2" style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}>Update</button>
                <button type="button" className="px-4 py-2 rounded" style={{ backgroundColor: currentTheme.surface, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }} onClick={() => setUpdateModalOpen(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Popup */}
      {isDeletePopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="p-4 rounded shadow-lg text-center" style={{ backgroundColor: currentTheme.surface }}>
            <p className="font-bold" style={{ color: currentTheme.text }}>Confirm Deletion</p>
            <p style={{ color: currentTheme.textSecondary }}>Are you sure you want to delete this user?</p>
            <div className="flex justify-center mt-4">
              <button
                onClick={confirmDelete}
                className={`px-4 py-2 rounded mr-2 ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ backgroundColor: currentTheme.error, color: 'white' }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Confirm'}
              </button>
              <button
                onClick={() => setDeletePopupOpen(false)}
                className="px-4 py-2 rounded"
                style={{ backgroundColor: currentTheme.surface, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;